import React, { ReactNode } from 'react';
import { compose } from 'redux';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import { AppState } from '@om-fe/core/core/types';
import { connect } from 'react-redux';
import preLoader from '@om-fe/core/core/bootstrap/preload';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import {
    FavoriteMatchListDefaultProps,
    FavoriteMatchListPublicProps,
    FavoriteMatchListStoreModel,
    FavoriteMatchListStoreProps,
} from './favorite-match-list.constants';
import { areFavoriteMatchListTopicPropsEqual, getFavoriteMatchListSliceName } from './favorite-match-list.utils';
import { getFavoriteMatchList, removeFavoriteMatchList, updateFavoriteMatchList } from './favorite-match-list.actions';
import MatchList from '../../match-list';
import getSlice from '@om-fe/core/utils/get-slice';
import { DisciplineLiveStatus, SessionInfoModel } from '@om-fe/models';
import { BemMethod, createBem } from '@om-fe/utils';
import { GeneralIcon } from '@om-fe/widgets/dist/components/icon/general-icon';
import { FavoritesStoreState } from '@om-fe/core/components/favorite-button/favorite-state.constants';
import { favoriteSlice } from '@om-fe/core/components/favorite-button/favorite-state.store';

interface FavoriteMatchListState {
    isExpanded: boolean;
}

type FavoriteMatchListProps = Partial<FavoriteMatchListDefaultProps>
    & FavoriteMatchListPublicProps
    & FavoriteMatchListStoreProps
    & I18nContextInterface;

const bem: BemMethod = createBem('MatchListMixed');

class FavoriteMatchListComponent extends React.Component<FavoriteMatchListProps, FavoriteMatchListState> {

    static defaultProps: FavoriteMatchListDefaultProps = {
        hasHeader: false,
    }

    state: FavoriteMatchListState = {
        isExpanded: true,
    }

    toggleIsExpanded = ():void => {
        this.setState({
            isExpanded: !this.state.isExpanded,
        })
    }

    getFavoriteMatchListNode(): ReactNode {
        const { filteredSportIds, instanceId, sliceKey, itemLayout } = this.props;

        return (
            <>
                {filteredSportIds?.map((disciplineId: string) => (
                    <MatchList
                        {...this.props}
                        key={disciplineId}
                        limitNumberOfEvents={undefined}
                        instanceId={instanceId}
                        liveStatus={DisciplineLiveStatus.LIVE}
                        sliceKey={`${sliceKey}.disciplines[${disciplineId}]`}
                        disciplineId={disciplineId}
                        showMarketsName={true}
                        showMixedMatchListHeader={true}
                        itemLayout={itemLayout}
                        showMatchListHeader={false}
                        showMarketInMatchListGroupHeader={true}
                    />
                ))}
            </>
        );
    }

    getFavoriteMatchListHeaderNode(): ReactNode {
        const { gettext, favoriteMatchIds } = this.props;
        const { isExpanded } = this.state;

        return (
            <div className={bem('Header')}>
                <button className={bem('CollapseButton')} onClick={this.toggleIsExpanded}>
                    <span className={bem('Title')}>{gettext('Favorites')}</span>
                    <span className={bem('Counter')} >{favoriteMatchIds!.length}</span>
                    <GeneralIcon name={isExpanded ? 'arr-up' : 'arr-down'} />
                </button>
            </div>
        )
    }

    render(): React.ReactNode {
        const { gettext, filteredSportIds, hasHeader } = this.props;
        const { isExpanded } = this.state;

        if (!filteredSportIds || !filteredSportIds.length) {

            if (hasHeader) {
                return null;
            }

            return (
                <div className={bem('EmptyList')}>
                    {gettext('No matches currently available')}
                </div>
            );
        }

        return (
            <div className={bem()}>
                {hasHeader && this.getFavoriteMatchListHeaderNode()}
                {(!hasHeader || (hasHeader && isExpanded)) && this.getFavoriteMatchListNode()}
            </div>
        );
    }
}

const FavoriteMatchList: React.ComponentType<FavoriteMatchListPublicProps> = compose<React.ComponentType<FavoriteMatchListPublicProps>>(
    withTranslation<FavoriteMatchListPublicProps>(),
    preLoader<FavoriteMatchListPublicProps & I18nContextInterface>({
        preload({ props, dispatch }) {

            const { instanceId, userID, liveStatus, isDefault, mainMarketsLimit } = props;

            dispatch(getFavoriteMatchList(instanceId, userID, liveStatus, isDefault, mainMarketsLimit));
        },
        getReadySlice({ instanceId }: FavoriteMatchListPublicProps): string {
            const slice: string = getFavoriteMatchListSliceName(instanceId);

            return `${slice}.ready`;
        },
        update({ props, prevProps, dispatch }): void {
            const { instanceId, userID, liveStatus, isDefault, mainMarketsLimit } = props;

            if (instanceId !== prevProps.instanceId) {
                dispatch(removeFavoriteMatchList(prevProps.instanceId));
                dispatch(getFavoriteMatchList(instanceId, userID, liveStatus, isDefault, mainMarketsLimit));
            } else if (!areFavoriteMatchListTopicPropsEqual(prevProps, props)) {
                dispatch(updateFavoriteMatchList(instanceId, userID, liveStatus, isDefault, mainMarketsLimit));
            }
        },
        destroy({ props, dispatch }) {
            const { instanceId } = props;

            dispatch(removeFavoriteMatchList(instanceId));
        },
    }),
    connect<FavoriteMatchListStoreProps, undefined, FavoriteMatchListPublicProps>(
        (state: AppState, props: FavoriteMatchListPublicProps): FavoriteMatchListStoreProps => {
            const { instanceId } = props;
            const sliceKey: string = getFavoriteMatchListSliceName(instanceId);
            const sessionInfo: SessionInfoModel | undefined = state.session && state.session.info;
            const isAuthenticated: boolean = !!(sessionInfo && sessionInfo.isAuthenticated);

            const slice: FavoriteMatchListStoreModel | undefined = getSlice<FavoriteMatchListStoreModel>(state, sliceKey);
            const favoriteEventsSlice: FavoritesStoreState | undefined = getSlice<FavoritesStoreState>(state, favoriteSlice);
            const favoriteMatchIds: string [] | undefined = favoriteEventsSlice && Object.keys(favoriteEventsSlice.favoriteMatchItems);


            if (!slice) {
                return {
                    sliceKey,
                    ready: false,
                    isAuthenticated,
                };
            }

            return {
                sliceKey,
                filteredSportIds: slice.filteredSportIds,
                ready: slice.ready,
                isAuthenticated,
                favoriteMatchIds,
            };
        },
    ),
)(FavoriteMatchListComponent);
export default FavoriteMatchList;
